import React from 'react';
import SidebarParametres from '../../../layout/SidebarParametres';
import { useOAD } from '../../../layout/OADLayout';
import SelecteurProduction, {
  productionParDefaut,
} from '../../shared/components/SelecteurProduction';
import { RCP_OPTIONS, COULEURS_EAU } from '../../shared/constants';
import { api } from '../../shared/api/api';
import { libelleHorizon } from '../../shared/utils/horizon';
import { useApiData } from '../../shared/hooks/useFetch';
import LimiteErreur from '../../shared/components/LimiteErreur';
import CarteRisquePRA from '../../shared/charts/CarteRisquePRA';
import LegendeCarte from '../../shared/charts/LegendeCarte';

// PRA/RA/Département seulement pour ces 3 pages "cartes mock" (pas de
// Région), à la demande d'Aliénor.
const ECHELLES = [
  { value: 'pra', label: 'Petite région agricole' },
  { value: 'ra', label: 'Région agricole' },
  { value: 'departement', label: 'Département' },
];

/**
 * Comparer / Eau : 2 cartes (2035/2050) pour la production choisie — écart de
 * besoin en eau par rapport à aujourd'hui. Négatif = bleu (besoin en baisse),
 * positif = rouge (besoin en hausse).
 * ⚠️ Nouvelle carte, mock (pas de route confirmée).
 */
export default function ComparerEau() {
  const { idExploit } = useOAD();
  const [production, setProduction] = React.useState(productionParDefaut);
  const [rcp, setRcp] = React.useState(RCP_OPTIONS[0].value);
  const [echelle, setEchelle] = React.useState(ECHELLES[0].value);

  const parametres = React.useMemo(
    () => ({
      idExploitation: idExploit,
      type: production.type,
      idProduction: production.id,
      semis: production.semis,
      alimentation: production.alimentation,
      abreuvement: production.abreuvement,
      rcp,
      echelle,
    }),
    [idExploit, production, rcp, echelle]
  );

  const { data, loading, error } = useApiData(api.getCartesEau, parametres, {
    skip: !idExploit || !production.id,
  });
  // Object.keys() ne respecte pas l'ordre d'insertion pour les clés qui
  // ressemblent à des entiers ("2" passe toujours avant "reference", quel
  // que soit l'ordre réel) : on fige l'ordre explicitement plutôt que de
  // faire confiance à Object.keys.
  const ORDRE_HORIZONS = { classique: [2035, 2050], tracc: ['reference', '2', '2.7'] };
  const horizons = data?.cartes
    ? (rcp === 'tracc' ? ORDRE_HORIZONS.tracc : ORDRE_HORIZONS.classique).filter((h) => data.cartes[h])
    : [];

  const { min, max } = React.useMemo(() => {
    if (!data?.cartes) return { min: 0, max: 0 };
    const valeurs = horizons.flatMap(
      (h) => data.cartes[h]?.data?.features?.map((f) => Number(f.properties.ecartBesoin)) ?? []
    );
    return valeurs.length ? { min: Math.min(...valeurs), max: Math.max(...valeurs) } : { min: 0, max: 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <div className="oad-contenu">
      <SidebarParametres
        niveau1="comparer-comprendre"
        niveau2="comparer"
        niveau3="eau"
        idExploit={idExploit}
      >
        <SelecteurProduction valeur={production} onChange={setProduction} />

        <label className="sidebar-champ">
          <span className="sidebar-label">Géographie</span>
          <select value={echelle} onChange={(e) => setEchelle(e.target.value)}>
            {ECHELLES.map((e) => (
              <option key={e.value} value={e.value}>{e.label}</option>
            ))}
          </select>
        </label>

        <fieldset className="sidebar-champ sidebar-groupe">
          <legend className="sidebar-label">Scénarios climatiques</legend>
          {RCP_OPTIONS.map((option) => (
            <label key={option.value} className="sidebar-radio">
              <input
                type="radio"
                name="scenario-clim-comparer-eau"
                value={option.value}
                checked={rcp === option.value}
                onChange={(e) => setRcp(e.target.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>
      </SidebarParametres>

      <main className="oad-cards">
        <section className="oad-card">
          <h2 className="tdb-section-titre">Écart de besoin en eau</h2>
          {error && <p className="tdb-erreur">{error}</p>}
          {loading && <p className="tdb-chargement">Chargement…</p>}

          {data?.cartes && (
            <>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {horizons.map((horizon) => (
                  <div key={horizon} style={{ flex: '1 1 320px', minWidth: 280 }}>
                    <h3 className="ig-camembert-titre">{libelleHorizon(rcp, horizon)}</h3>
                    <LimiteErreur cle={`eau-carte-${horizon}`}>
                      <CarteRisquePRA
                        data={data.cartes[horizon].data}
                        pointExploitation={data.pointExploitation}
                        champ="ecartBesoin"
                        continu={{ type: 'diverge', ...COULEURS_EAU, min, max }}
                        unite="%"
                        afficherLegende={false}
                      />
                    </LimiteErreur>
                  </div>
                ))}
              </div>
              <LegendeCarte continu={{ type: 'diverge', ...COULEURS_EAU, min, max }} min={min} max={max} unite="%" />
            </>
          )}
        </section>
      </main>
    </div>
  );
}
